import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useManageMatterRequestContext } from '../context/ManageMatterRequest';
import { useEmailCategoryTagger } from './useEmailCategoryTagger';
import { useCreateNote } from './useCreateNote';
import { useCreateNoteAttachment } from './useCreateNoteAttachment';
import { generateEmlFromMail, generateEmlWithoutAttachments } from '../constants/utilityFunctions';
import { checkAttachmentSizeCompliance } from '../constants/transactionUtility';
import { IMatterRequest } from '../interface/matterRequest';
import { INote } from '../interface/notes';
import { showToast } from '../components/utils/toast';
import { SignificanceTypeValue } from '../constants/notes';
import { MAX_FILE_SIZE } from '../constants/constants';

interface UseMatterRequestMailAsNoteOptions {
  onMatterRequestCreated?: () => void;
}

export const useMatterRequestMailAsNote = (options?: UseMatterRequestMailAsNoteOptions) => {
  const { onMatterRequestCreated } = options ?? {};
  const { t } = useTranslation();
  const { matterRequest, updateManageMatterRequest } = useManageMatterRequestContext();
  const { getItemId, tagEmailWithCategory } = useEmailCategoryTagger();
  const createNoteMutation = useCreateNote();
  const createNoteAttachmentMutation = useCreateNoteAttachment();

  const [isMailAsNoteLoading, setIsMailAsNoteLoading] = useState(false);
  const [confirmationForLargeAttachmentsModal, setConfirmationForLargeAttachmentsModal] =
    useState(false);
  const [showFileSizeWarning, setShowFileSizeWarning] = useState(false);

  const handleSwitchMailAsNote = async (value: boolean) => {
    let mailAsNote = value;
    const item = Office.context.mailbox.item;
    const itemId = item?.itemId ?? (await getItemId(item)) ?? '';

    if (value && itemId) {
      setIsMailAsNoteLoading(true);
      const { file, error } = await generateEmlWithoutAttachments(matterRequest?.subject);
      if (error) {
        setIsMailAsNoteLoading(false);
        showToast('error', t(error));
        updateManageMatterRequest({
          ...matterRequest,
          mailAsNote: false,
        } as unknown as IMatterRequest);
        return;
      }
      if (file && file.size > MAX_FILE_SIZE) {
        setIsMailAsNoteLoading(false);
        setShowFileSizeWarning(true);
        updateManageMatterRequest({
          ...matterRequest,
          mailAsNote: false,
          mailAsNotewithoutAttachmentsWarning: false,
          mailAsNoteWithoutAttachments: false,
        } as unknown as IMatterRequest);
        return;
      }
      const { file: emlFile } = await generateEmlFromMail(matterRequest?.subject);
      const checkAttachmentSizes = await checkAttachmentSizeCompliance(itemId).finally(() => {
        setIsMailAsNoteLoading(false);
      });
      if (!checkAttachmentSizes || emlFile.size > MAX_FILE_SIZE) {
        setConfirmationForLargeAttachmentsModal(true);
        return;
      }
      mailAsNote = true;
    }

    updateManageMatterRequest({
      ...matterRequest,
      mailAsNote,
      mailAsNotewithoutAttachmentsWarning: false,
      mailAsNoteWithoutAttachments: false,
    } as unknown as IMatterRequest);
  };

  const handleCreateNoteAttachment = (noteId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    createNoteAttachmentMutation.mutate(
      { notesId: noteId, data: formData },
      {
        onSuccess: () => {
          updateManageMatterRequest(null, true);
        },
      },
    );
  };

  const handleMatterRequestIsFromMailOrAppointment = async (
    matterRequestRecord: IMatterRequest,
  ) => {
    await tagEmailWithCategory(`[CSI - ${t('matterRequest:matterRequest')}]`, {
      matterRequestId: matterRequestRecord.id ?? '',
    });
    onMatterRequestCreated?.();

    if (!matterRequest?.mailAsNote) {
      updateManageMatterRequest(null, true);
      return;
    }

    const { file, subject } = matterRequest?.mailAsNoteWithoutAttachments
      ? await generateEmlWithoutAttachments(matterRequest?.subject)
      : await generateEmlFromMail(matterRequest?.subject);

    createNoteMutation.mutate(
      {
        note: subject,
        matterRequestId: matterRequestRecord.id,
        significance: SignificanceTypeValue.normal,
      },
      {
        onSuccess: (record: INote) => {
          handleCreateNoteAttachment(record.id, file as File);
        },
      },
    );
  };

  const handleConfirmLargeAttachments = () => {
    updateManageMatterRequest({
      ...matterRequest,
      mailAsNote: true,
      mailAsNoteWithoutAttachments: true,
      mailAsNotewithoutAttachmentsWarning: true,
    } as unknown as IMatterRequest);
    setConfirmationForLargeAttachmentsModal(false);
  };

  const handleCancelLargeAttachments = () => {
    updateManageMatterRequest({
      ...matterRequest,
      mailAsNote: false,
      mailAsNoteWithoutAttachments: false,
    } as unknown as IMatterRequest);
    setConfirmationForLargeAttachmentsModal(false);
  };

  return {
    isMailAsNoteLoading,
    confirmationForLargeAttachmentsModal,
    showFileSizeWarning,
    handleSwitchMailAsNote,
    handleMatterRequestIsFromMailOrAppointment,
    handleConfirmLargeAttachments,
    handleCancelLargeAttachments,
    closeFileSizeWarning: () => setShowFileSizeWarning(false),
  };
};
